// Shortcut "N": the page's "Novo …" action. Every list marks it the same way (@acme/ui styles.css): `.ui-new` on the
// head button (desktop) and `.ui-fab` on the floating "+" (below 1024px), with the same action and permission. So one
// handler in the AppShell serves every page, and a page without the button (no permission) does nothing.

export const NEW_ACTION_SELECTOR = '.ui-new, .ui-fab'
const CONTENT_SELECTOR = '.shell__content'

export function isNewShortcut(event: KeyboardEvent): boolean {
  return (event.key === 'n' || event.key === 'N') && !event.ctrlKey && !event.altKey && !event.metaKey && !event.repeat && !event.defaultPrevented
}

// Typing in a field (search, form, the rich text editor) types the letter.
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  if (target.closest('input, textarea, select')) return true
  return target.closest('[contenteditable]:not([contenteditable="false"])') !== null
}

// A dialog is open (UiDialog and UiConfirmDialog only exist while open): never a new form on top of another one.
export function hasOpenModal(root: ParentNode): boolean {
  return root.querySelector('[aria-modal="true"], [role="dialog"], [role="alertdialog"]') !== null
}

const usable = (element: Element) => element.getClientRects().length > 0 && !(element as HTMLButtonElement).disabled && element.getAttribute('aria-disabled') !== 'true'

// The visible "Novo …" of the page content: the head button, or the floating one where the head button is hidden.
export function findNewAction(root: ParentNode): HTMLElement | null {
  const content = root.querySelector(CONTENT_SELECTOR) ?? root
  const head = Array.from(content.querySelectorAll<HTMLElement>('.ui-new')).find(usable)
  return head ?? Array.from(content.querySelectorAll<HTMLElement>('.ui-fab')).find(usable) ?? null
}

// Returns whether it acted.
export function handleNewShortcut(event: KeyboardEvent, root: ParentNode): boolean {
  if (!isNewShortcut(event) || isTypingTarget(event.target) || hasOpenModal(root)) return false
  const action = findNewAction(root)
  if (!action) return false
  event.preventDefault()
  action.click()
  return true
}

// Makes the shortcut discoverable: "Novo cliente (N)" on hover and aria-keyshortcuts for screen readers. Set lazily
// (on pointer or focus), so the pages need nothing.
export function describeNewShortcut(target: EventTarget | null): void {
  if (!(target instanceof Element)) return
  const action = target.closest(NEW_ACTION_SELECTOR)
  if (!action || action.hasAttribute('aria-keyshortcuts')) return
  action.setAttribute('aria-keyshortcuts', 'N')
  if (!action.getAttribute('title')) {
    const label = (action.getAttribute('aria-label') ?? action.textContent ?? '').trim()
    action.setAttribute('title', label ? `${label} (N)` : 'N')
  }
}
